import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export interface AgentConfig {
  agent_id: string;
  enabled: boolean;
  frequency: string;
  settings: Record<string, any>;
}

export interface AgentMission {
  agent_id: string;
  mission_type: string;
  status: string;
  output_data: Record<string, any>;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface AgentLog {
  agent_id: string;
  action: string;
  log_type: string;
  details: Record<string, any>;
  created_at: string;
}

export function useNanoCorp() {
  const { user } = useAuth();
  const [configs, setConfigs] = useState<AgentConfig[]>([]);
  const [missions, setMissions] = useState<AgentMission[]>([]);
  const [logs, setLogs] = useState<AgentLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [runningAgent, setRunningAgent] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [configsRes, missionsRes, logsRes] = await Promise.all([
        fetch(`${API_URL}/api/nanocorp/configs`, { credentials: "include" as RequestCredentials }),
        fetch(`${API_URL}/api/nanocorp/missions`, { credentials: "include" as RequestCredentials }),
        fetch(`${API_URL}/api/nanocorp/logs`, { credentials: "include" as RequestCredentials }),
      ]);
      if (configsRes.ok) setConfigs((await configsRes.json()).configs || []);
      if (missionsRes.ok) setMissions((await missionsRes.json()).missions || []);
      if (logsRes.ok) setLogs((await logsRes.json()).logs || []);
    } catch {}
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleAgent = async (agentId: string) => {
    const current = configs.find((c) => c.agent_id === agentId);
    if (!current) return;
    const newEnabled = !current.enabled;
    try {
      await fetch(`${API_URL}/api/nanocorp/configs/${agentId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include" as RequestCredentials,
        body: JSON.stringify({ enabled: newEnabled }),
      });
      setConfigs((prev) => prev.map((c) => (c.agent_id === agentId ? { ...c, enabled: newEnabled } : c)));
      toast.success(`Agent ${agentId} ${newEnabled ? "activé" : "désactivé"}`);
    } catch {
      toast.error("Erreur de mise à jour");
    }
  };

  const runAgent = async (agentId: string, action: string) => {
    if (runningAgent) return;
    setRunningAgent(agentId);
    try {
      const res = await fetch(`${API_URL}/api/nanocorp/run/${agentId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include" as RequestCredentials,
        body: JSON.stringify({ action }),
      });
      if (!res.ok) throw new Error("Mission failed");
      const data = await res.json();
      toast.success(`Mission ${agentId} terminée avec succès`);
      await fetchData();
      return data;
    } catch (err: any) {
      toast.error(`Erreur agent ${agentId}: ${err.message}`);
      return null;
    } finally {
      setRunningAgent(null);
    }
  };

  const getAgentStats = (agentId: string) => {
    const agentMissions = missions.filter((m) => m.agent_id === agentId);
    const completed = agentMissions.filter((m) => m.status === "completed").length;
    const running = agentMissions.filter((m) => m.status === "running").length;
    const successRate = agentMissions.length > 0 ? (completed / agentMissions.length) * 100 : 0;
    const lastMission = agentMissions[0];
    return { completed, running, successRate, lastMission };
  };

  return { configs, missions, logs, loading, runningAgent, toggleAgent, runAgent, getAgentStats, refreshData: fetchData };
}
